// WHY (#590): web-side display helper that mirrors the backend's
//   `package-calc.ts`. Backend remains authoritative on save (Decimal
//   arithmetic + bounds checks), this module exists so the editor can
//   show running totals without waiting for a round-trip. The same
//   formulas are used for the read-only breakdown on the items tab.

import type { DraftPackageRow, RequestItemPackageView } from './types.js';

const M3_PER_CM3 = 1 / 1_000_000;

function asNumber(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export interface PackageRowLike {
  widthCm: number;
  depthCm: number;
  heightCm: number;
  weightKg: number;
  count: number;
}

export function computePackageVolumeM3(row: PackageRowLike): number {
  return asNumber(row.widthCm) * asNumber(row.depthCm) * asNumber(row.heightCm) * M3_PER_CM3;
}

export function computeRowVolumeM3(row: PackageRowLike): number {
  return computePackageVolumeM3(row) * asNumber(row.count);
}

export function computeRowWeightKg(row: PackageRowLike): number {
  return asNumber(row.weightKg) * asNumber(row.count);
}

export interface ItemTotals {
  weight: number;
  volume: number;
  quantity: number;
}

export function computeItemTotals(rows: ReadonlyArray<PackageRowLike>): ItemTotals {
  let weight = 0;
  let volume = 0;
  let quantity = 0;
  for (const row of rows) {
    weight += computeRowWeightKg(row);
    volume += computeRowVolumeM3(row);
    quantity += asNumber(row.count);
  }
  // INVARIANT (#590, #1304): mirror backend rounding to 0.0001 m³ so the
  //   display matches what gets persisted on save.
  return {
    weight: round4(weight),
    volume: round4(volume),
    quantity: Math.trunc(quantity),
  };
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

export function totalsFromDraftRows(rows: ReadonlyArray<DraftPackageRow>): ItemTotals {
  return computeItemTotals(rows);
}

export function totalsFromViewRows(rows: ReadonlyArray<RequestItemPackageView>): ItemTotals {
  return computeItemTotals(rows);
}
